import {
  ExtendableImage,
  ExtendableImageProps,
} from '@design-system/extendable-image';
import { ProductCardProps } from '@design-system/product-card';
import { QuantityButtonProps } from '@design-system/quantity-button';
import { setLocalStorageData } from '@phx-husky/local-storage';
import { navigate } from 'gatsby';
import { kebabCase } from 'lodash';
import React from 'react';

import ShopifyBlock from '~/components/ShopifyBlock';
import { AddToCartModalProps } from '~/components/ShopifyBlock/NotificationModal/models';
import TMPLinkWrap from '~/components/TMPLinkWrap';
import { buttonMapper } from '~/mappers/button';
import { headingBlockMapper } from '~/mappers/headingBlock';
import { imageMapper } from '~/mappers/image';
import {
  ItemListingProductCardCategory,
  ItemListingProductCardRef,
} from '~/mappers/itemListing/models';
import { quantityButtonMapper } from '~/mappers/quantityButton';
import { gtmService } from '~/services/gtm';
import { GTM_LOCALSTORAGE_DATA } from '~/utils/constants';
import {
  nlToBr,
  snakeToCamelWithSpecialCases,
  validateTextLength,
} from '~/utils/stringUtils/stringUtils';

import {
  getDescriptionProps,
  ProductCardCategory,
  ProductCardDataCommon,
  ProductCardRef,
} from './models';

const NO_DESCRIPTION = 'none';
const CMS_DESCRIPTION = 'page data';

const getDescription = ({
  descriptionField,
  cmsDescription,
  productData,
}: getDescriptionProps): string | null => {
  switch (descriptionField) {
    case NO_DESCRIPTION:
      return null;
    case CMS_DESCRIPTION:
      return cmsDescription;
    default:
      return productData[descriptionField];
  }
};

const productCardMapper = (
  {
    className,
    productData,
    listingData: { label, description: cmsDescription },
    fullPath,
    shopifyBlock,
    onClickProductCard,
    categories,
  }: ProductCardDataCommon,
  {
    imageSettings,
    cardContentUI,
    variant,
    showLabels,
    descriptionField,
    showCategory,
  }:
    | Omit<ItemListingProductCardCategory, 'type' | 'typeName'>
    | Omit<ItemListingProductCardRef, 'type' | 'typeName'>
): ProductCardProps => {
  const {
    descriptionShort,
    heroImage,
    productTitle,
    aromaScent,
    netContentStatement,
  } = productData;

  return {
    ...(className && {
      className,
    }),
    ...(heroImage && {
      cardMedia: {
        image: imageMapper({
          ...{
            alt: heroImage.alt,
            image: {
              gatsbyImageData:
                heroImage.localAsset.childImageSharp.gatsbyImageData,
              url: heroImage.url,
            },
          },
          ...imageSettings,
        }),
        mediaVariant: imageSettings.rounded ? 'rounded' : 'standard',
      },
    }),
    link: {
      component: TMPLinkWrap,
      props: {
        href: fullPath,
        onClick: onClickProductCard ? () => onClickProductCard() : undefined,
      },
    },
    cardContent: {
      headingBlock: headingBlockMapper({
        ui: cardContentUI,
        headline: productTitle,
        descriptionRte: getDescription({
          descriptionField,
          cmsDescription,
          productData,
        }),
        detail: null,
        link: [],
        button: [],
      }),
      ...(showCategory &&
        categories?.[0]?.title && {
          category: categories?.[0]?.title,
        }),
    },
    ...(showLabels &&
      label[0]?.content && {
        cardLabel: {
          labelContent: label[0].content,
          srOnlyLabelPrefix: label[0].srPrefix,
          labelVariant: label[0].variant,
          labelSize: label[0].size,
        },
      }),
    variant,
    ...(shopifyBlock?.length && {
      shopifyBlock: {
        soldOut: shopifyBlock[0].soldOut,
        addToCartButton: {
          ...shopifyBlock[0].addToCartButton,
          text: validateTextLength(shopifyBlock[0].addToCartButton),
        },
        buyNowButton: {
          ...(shopifyBlock[0].buyNowButton && {
            ...shopifyBlock[0].buyNowButton,
            text: validateTextLength(shopifyBlock[0].buyNowButton),
          }),
        },
        addToCartModal: shopifyBlock[0].addToCartModal[0],
      },
      shopifyBlockComponent: (
        <ShopifyBlock
          isUpSellVariant={shopifyBlock[0].isUpSellVariant}
          discountedPrice={shopifyBlock[0].discountedPrice}
          discountedPriceUI={shopifyBlock[0].discountedPriceUI}
          availableForSale={shopifyBlock[0].availableForSale}
          inventoryQuantity={shopifyBlock[0].inventoryQuantity}
          currentPrice={shopifyBlock[0].currentPrice}
          currentPriceUI={shopifyBlock[0].currentPriceUI}
          addToCartButton={{
            ...buttonMapper({
              ...shopifyBlock[0].addToCartButton,
              text: validateTextLength(shopifyBlock[0].addToCartButton),
            }),
            storefrontId: shopifyBlock[0].addToCartButton.storefrontId,
          }}
          buyNowButton={
            shopifyBlock[0].buyNowButton &&
            buttonMapper({
              ...shopifyBlock[0].buyNowButton,
              text: validateTextLength(shopifyBlock[0].buyNowButton),
            })
          }
          addToCartModal={
            {
              ...snakeToCamelWithSpecialCases(
                shopifyBlock[0].addToCartModal[0]
              ),
              continueShopping: buttonMapper(
                shopifyBlock[0].addToCartModal[0].continueShopping
              ),
              checkout: buttonMapper(
                shopifyBlock[0].addToCartModal[0].checkout
              ),
              handleCheckoutNavigation: () => {
                navigate(shopifyBlock[0].addToCartModal[0].checkout.link.href);
              },
              productName: productTitle,
              productImage: (
                <ExtendableImage
                  {...(imageMapper({
                    alt: heroImage.alt,
                    image: {
                      gatsbyImageData:
                        heroImage.localAsset.childImageSharp.gatsbyImageData,
                      url: heroImage.url,
                    },
                    objectFit: 'contain',
                  }) as ExtendableImageProps)}
                />
              ),
              productScent: aromaScent,
              productSize: netContentStatement,
            } as AddToCartModalProps
          }
          quantityButton={
            quantityButtonMapper(
              shopifyBlock[0].quantityButton[0]
            ) as QuantityButtonProps
          }
          description={nlToBr(descriptionShort)}
          className="shopify-block-plp"
        />
      ),
    }),
    categories: categories.map(({ title }) => kebabCase(title)),
  } as ProductCardProps;
};

export const productCardCategoryMapper = (
  {
    productData,
    listingData,
    fullPath,
    className,
    categories,
  }: ProductCardCategory,
  settings: Omit<ItemListingProductCardCategory, 'type' | 'typeName'>,
  shopifyBlock,
  index
): ProductCardProps => {
  const onCLickProductCard = () => {
    setLocalStorageData(GTM_LOCALSTORAGE_DATA.lastProductIndex, index + 1);
    gtmService.emitSelectItem(productData, { index: index + 1 });
  };

  return productCardMapper(
    {
      productData,
      listingData: listingData || {},
      fullPath,
      shopifyBlock,
      className,
      onClickProductCard: onCLickProductCard,
      categories,
    },
    settings
  );
};

export const productCardRefMapper = (
  {
    className,
    listingData,
    fields: { fullPath, productData },
    shopifyBlock,
    categories,
  }: ProductCardRef,
  settings: Omit<ItemListingProductCardRef, 'type' | 'typeName'>,
  index
  // eslint-disable-next-line sonarjs/no-identical-functions
): ProductCardProps => {
  const onCLickProductCard = () => {
    setLocalStorageData(GTM_LOCALSTORAGE_DATA.lastProductIndex, index + 1);
    gtmService.emitSelectItem(productData, { index: index + 1 });
  };

  return productCardMapper(
    {
      className,
      productData,
      listingData: listingData || {},
      fullPath,
      shopifyBlock,
      onClickProductCard: onCLickProductCard,
      categories,
    },
    settings
  );
};
